import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useProduct } from '~/modules/product/composables/useProduct';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import {
  useProductDetails,
  useShippingDetails,
  useRelationshipsDetails,
  usePricingBadgesDetails,
  useIdentifier,
} from '~/composables/useProductGetters';
import { getMemberProducts } from '~/utils/product';

export function useReactiveProductGetters() {
  const { productId } = useProduct();
  const store = useConsolidatedMemberStore();
  const { memberProduct } = storeToRefs(store);

  const productDetails = computed(() => useProductDetails(memberProduct.value?.detail || {}));
  const shippingDetails = computed(() => useShippingDetails(memberProduct.value?.shipping || {}));
  const relationshipsDetails = computed(() => useRelationshipsDetails(memberProduct.value?.relationships || {}));
  const pricingBadgesDetails = computed(() => usePricingBadgesDetails(memberProduct.value?.pricing || {}));
  const identifier = computed(() => useIdentifier(memberProduct.value));
  const bannerDetails = computed(() => memberProduct.value?.banner || {});

  function anyMemberProducts(cb) {
    if (!memberProduct.value) return false;
    return getMemberProducts(memberProduct.value).some(cb);
  }

  return {
    productId,
    isProductAvailable: computed(() => !!memberProduct.value?.availability?.available),
    isCcfpAvailable: computed(() => !!memberProduct.value?.ccfp?.ccfpEligible),
    bannerDetails,
    productUrl: computed(() => identifier.value.productUrl),
    topLevelCategoryID: computed(() => identifier.value.topLevelCategoryID),
    topLevelCategoryName: computed(() => identifier.value.topLevelCategoryName),
    productName: computed(() => productDetails.value.productName),
    productExclusiveName: computed(() => productDetails.value.productExclusiveName),
    productDescription: computed(() => productDetails.value.productDescription),
    secondaryDescription: computed(() => productDetails.value.secondaryDescription),
    brandName: computed(() => productDetails.value.brandName),
    brandDisplayName: computed(() => productDetails.value.brandDisplayName),
    brandUrl: computed(() => productDetails.value.brandUrl),
    bulletText: computed(() => productDetails.value.bulletText),
    bulletLinks: computed(() => productDetails.value.bulletLinks),
    dimensionsBulletText: computed(() => productDetails.value.dimensionsBulletText),
    dimensionsHeaderText: computed(() => productDetails.value.dimensionsHeaderText),
    processedProdDesc: computed(() => productDetails.value.processedProdDesc),
    numberOfColors: computed(() => productDetails.value.numberOfColors),
    typeName: computed(() => productDetails.value.typeName),
    swatchEmail: computed(() => productDetails.value.swatchEmail),
    metric: computed(() => productDetails.value.metric),
    pdfEmailDescription: computed(() => productDetails.value.pdfEmailDescription),
    isShippingAvailable: computed(() => shippingDetails.value.isShippingAvailable),
    notes: computed(() => shippingDetails.value.notes),
    freeShippingMessages: computed(() => shippingDetails.value.freeShippingMessages),
    marketPlaceProductShippingMessages: computed(() => shippingDetails.value.marketPlaceProductShippingMessages),
    returnConstraintMessage: computed(() => shippingDetails.value.returnConstraintMessage),
    stateRestrictionMessage: computed(() => shippingDetails.value.stateRestrictionMessage),
    giftWrapMessage: computed(() => shippingDetails.value.giftWrapMessage),
    specialGiftWrapMessage: computed(() => shippingDetails.value.specialGiftWrapMessage),
    surchargeMessage: computed(() => shippingDetails.value.surchargeMessage),
    shippingMessage: computed(() => shippingDetails.value.shippingMessage),
    bigTicketDeliveryMethods: computed(() => shippingDetails.value.bigTicketDeliveryMethods),
    taxonomy: computed(() => relationshipsDetails.value.taxonomy),
    badgesMap: computed(() => pricingBadgesDetails.value.badgesMap),
    anyMemberProducts,
    isSingleUpcProduct: computed(() => {
      if (!memberProduct.value) return false;
      return Object.keys(memberProduct.value?.traits?.traitsMaps?.upcMap || {}).length === 1;
    }),
    highlights: computed(() => productDetails.value.highlights || []),
  };
}
